import { Box, ButtonBase, Grid, Stack, Typography } from "@mui/material";
import {
  DateIndicator,
  DatePeriod,
  Filter,
  FilterValueType,
  Operator,
  QueryFilterTemplate,
} from "../../services/swagger";
import { useEffect, useState } from "react";
import OperatorSelectorButton from "../atoms/OperatorSelectorButton";
import FormInput from "./FormInput";
import useDidMountEffect from "../../hooks/useDidMountEffect";
import { useTranslation } from "react-i18next";
import DoDisturbOnOutlinedIcon from "@mui/icons-material/DoDisturbOnOutlined";
import FieldLabel from "../atoms/FieldLabel";
import DateIndicatorButton from "../atoms/DateIndicator";

interface EndAdornmentProps {
  type: FilterValueType;
  onChange: (operator: Operator) => void;
  onDelete?: () => void;
}

interface FilterInputProps {
  label: string;
  field: string;
  type: FilterValueType;
  list?: QueryFilterTemplate["listOptions"];
  onDelete?: () => void;
  onChange: (filter: Filter) => void;
  value: string | number | boolean;
  advancedFilters: boolean;
  operator: Operator;
  dateIndicator?: DateIndicator;
  datePeriod: DatePeriod;
}

type AllowedOperators = {
  [key in FilterValueType]: Operator[];
};

export const allowedOperators: AllowedOperators = {
  id: ["eq", "ne"],
  boolean: ["eq", "ne"],
  date: ["gt", "gte", "lt", "lte"],
  list: ["eq", "ne"],
  number: ["eq", "ne", "gt", "gte", "lt", "lte"],
  string: ["regex", "eq", "ne"],
};

function EndAdornment(props: EndAdornmentProps & { mr?: number }) {
  if (!props.onDelete) return null;
  return (
    <Box
      alignItems="center"
      sx={{ ml: 1, mr: props.mr, width: 20, height: 20 }}
    >
      <DoDisturbOnOutlinedIcon
        sx={{ cursor: "pointer" }}
        onClick={props.onDelete}
        color="error"
      />
    </Box>
  );
}

export default function FilterInput(props: FilterInputProps) {
  const { operator } = props;
  const { t: tEnums } = useTranslation("Enums");
  const { t: tFormInput } = useTranslation("FormInput");

  const [value, setValue] = useState<any>("");
  const handleChange = (filter?: Filter) => {
    props.onChange(
      filter || {
        field: props.field,
        operator,
        type: props.type,
        value,
      }
    );
  };

  const handleChangeOperator = (operator: Operator) => {
    handleChange({
      field: props.field,
      operator,
      type: props.type,
      value: props.value,
    });
  };

  const handleChangeDateIndicator = (dateIndicator: DateIndicator | "") => {
    handleChange({
      field: props.field,
      operator,
      type: props.type,
      value: !(
        props.value &&
        props.dateIndicator !== "last" &&
        dateIndicator === "last"
      )
        ? 1
        : props.value,
      dateOptions: dateIndicator
        ? {
            indicator: dateIndicator,
            period: props.datePeriod,
          }
        : undefined,
    });
  };

  // NOTE, last 13 months is bugged (shows everything)
  // last 13 weeks issue

  const handleChangeDatePeriod = (datePeriod: DatePeriod) => {
    handleChange({
      field: props.field,
      operator,
      type: props.type,
      value: props.value,
      dateOptions: {
        indicator: props.dateIndicator || DateIndicator.Last,
        period: datePeriod,
      },
    });
  };

  useDidMountEffect(() => {
    if (!props.advancedFilters) {
      handleChangeOperator(allowedOperators[props.type][0]);
    }
  }, [props.advancedFilters]);

  if (props.type === FilterValueType.List)
    return (
      <Stack direction={"column"}>
        <Stack spacing={"auto"} direction={"row"} sx={{ alignItems: "center" }}>
          <FieldLabel fontSize={12}>{props.label}</FieldLabel>
          {props.advancedFilters && (
            <OperatorSelectorButton
              type={props.type}
              value={operator}
              onChange={(newOperator) => handleChangeOperator(newOperator)}
              size={11}
            />
          )}
        </Stack>
        <FormInput
          type="list"
          field={props.field}
          label={""}
          placeholder="Any"
          value={props.value}
          list={(props.list || []).map((item) => ({
            ...item,
            name: item.translatableEnum
              ? tEnums(item.translatableEnum)
              : item.name,
          }))}
          endAdornment={
            <EndAdornment
              type={props.type}
              onChange={(newOperator) => handleChangeOperator(newOperator)}
              onDelete={props.onDelete}
              mr={-1}
            />
          }
          onChange={(value) => {
            setValue(value || null);
            props.onChange?.({
              field: props.field,
              operator,
              type: props.type,
              value: value || null,
            });
          }}
        />
      </Stack>
    );
  else if (props.type === FilterValueType.Boolean)
    return (
      <Stack direction={"column"}>
        <Stack spacing={"auto"} direction={"row"} sx={{ alignItems: "center" }}>
          <FieldLabel fontSize={12}>{props.label}</FieldLabel>
          {props.advancedFilters && (
            <OperatorSelectorButton
              type={props.type}
              value={operator}
              onChange={(newOperator) => handleChangeOperator(newOperator)}
              size={11}
            />
          )}
        </Stack>
        <FormInput
          type="list"
          field={props.field}
          label={""}
          placeholder="Any"
          value={props.value}
          list={[
            { name: tFormInput("True"), value: "true" },
            { name: tFormInput("False"), value: "false" },
          ]}
          endAdornment={
            <EndAdornment
              type={props.type}
              onChange={(newOperator) => handleChangeOperator(newOperator)}
              onDelete={props.onDelete}
              mr={-1}
            />
          }
          onChange={(value) => {
            setValue(value ?? null);
            props.onChange?.({
              field: props.field,
              operator,
              type: props.type,
              value: value ?? null,
            });
          }}
        />
      </Stack>
    );
  else if (props.type === FilterValueType.Date)
    return (
      <Stack direction={"column"}>
        <Stack spacing={"auto"} direction={"row"} sx={{ alignItems: "center" }}>
          <FieldLabel fontSize={12}>{props.label}</FieldLabel>
          <Stack direction="row" spacing={1}>
            <DateIndicatorButton
              value={props.dateIndicator}
              onChange={(newDateIndicator) =>
                handleChangeDateIndicator(newDateIndicator)
              }
              size={11}
            />
            {!props.dateIndicator && (
              <OperatorSelectorButton
                type={props.type}
                value={operator}
                onChange={(newOperator) => handleChangeOperator(newOperator)}
                size={11}
              />
            )}
          </Stack>
        </Stack>
        <Grid container spacing={1}>
          {(!props.dateIndicator ||
            props.dateIndicator === DateIndicator.Last) && (
            <Grid size={props.dateIndicator ? 6 : 12}>
              <FormInput
                field={props.field}
                label={""}
                placeholder={props.label}
                value={props.value}
                type={props.dateIndicator === "last" ? "number" : "date"}
                inputType={props.dateIndicator === "last" ? "number" : "date"}
                onChange={(value) => {
                  setValue(value);
                  props.onChange?.({
                    field: props.field,
                    operator,
                    type: props.type,
                    value,
                    dateOptions:
                      props.dateIndicator && props.datePeriod
                        ? {
                            indicator: props.dateIndicator,
                            period: props.datePeriod,
                          }
                        : undefined,
                  });
                }}
              />
            </Grid>
          )}
          {props.dateIndicator && (
            <Grid size={props.dateIndicator === DateIndicator.This ? 12 : 6}>
              <FormInput
                field={`${props.field}-datePeriod`}
                label={""}
                value={props.datePeriod}
                type="list"
                list={[
                  { name: tEnums("DatePeriod.Day"), value: DatePeriod.Day },
                  {
                    name: tEnums("DatePeriod.Week"),
                    value: DatePeriod.Week,
                  },
                  {
                    name: tEnums("DatePeriod.Month"),
                    value: DatePeriod.Month,
                  },
                  {
                    name: tEnums("DatePeriod.Year"),
                    value: DatePeriod.Year,
                  },
                ]}
                onChange={(value) => {
                  setValue(value);
                  handleChangeDatePeriod(value as DatePeriod);
                }}
              />
            </Grid>
          )}
        </Grid>
      </Stack>
    );
  else
    return (
      <Stack direction={"column"}>
        <Stack spacing={"auto"} direction={"row"} sx={{ alignItems: "center" }}>
          <FieldLabel fontSize={12}>{props.label}</FieldLabel>
          {props.advancedFilters && (
            <OperatorSelectorButton
              type={props.type}
              value={operator}
              onChange={(newOperator) => handleChangeOperator(newOperator)}
              size={11}
            />
          )}
        </Stack>
        <FormInput
          field={props.field}
          label={""}
          placeholder={props.label}
          value={props.value}
          endAdornment={
            <EndAdornment
              type={props.type}
              onChange={(newOperator) => handleChangeOperator(newOperator)}
              onDelete={props.onDelete}
            />
          }
          type={props.type}
          inputType={props.type}
          onChange={(value) => {
            setValue(value);
            props.onChange?.({
              field: props.field,
              operator,
              type: props.type,
              value,
            });
          }}
        />
      </Stack>
    );
}
